import { createFileRoute } from '@tanstack/react-router'
import { useLingui } from '@lingui/react/macro'
import { PlugIcon } from 'lucide-react'
import { noop, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useModalSlot } from '@/components/custom/modal-slot'
import { formatDateLocale } from '@/lib/utils'
import { trpc } from '@/main'
import { CopyableCredential } from '@/routes/_app/-components/api/copyable-credential'
import { AiAppsDialogPending } from '@/routes/_app/-components/shared/dialog-pending'
import type { Locale } from 'shared/locales'

/** The user's connected AI apps (MCP clients): what each acts as, and how to connect one — docs/notes/server/mcp.md. */
export const Route = createFileRoute('/@modal/settings/ai-apps')({
  staticData: { slotScope: '/_app', slotOutlet: 'userMenu' },
  loader: ({ context }) => {
    // The server refuses these reads to whoever it gives no assistant; the menu row is not shown to them either.
    if (!context.queryClient.getQueryData(context.sessionUserQueryOptions.queryKey)?.assistantEnabled) return
    // query: direct the main router never matches a slot route, so no route context can hold this read
    // prefetch: speculative warms the list on intent, so the dialog paints it at once
    context.queryClient.query(trpc.mcp.listConnections.queryOptions()).catch(noop)
  },
  pendingComponent: AiAppsDialogPending,
  component: AiAppsSlot,
})

function AiAppsSlot() {
  const slot = useModalSlot(Route)
  const { t } = useLingui()
  return (
    <Dialog open={slot.open} onOpenChange={slot.onOpenChange}>
      <DialogContent forceBackdrop className="flex flex-col gap-4 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PlugIcon className="text-primary size-5" />
            {t`Connected AI apps`}
          </DialogTitle>
          <DialogDescription>
            {t`AI apps such as Claude or ChatGPT can use your D’Anchiano data from their own chat, with what you can see here. They receive real names and scores.`}
          </DialogDescription>
        </DialogHeader>
        <Connections />
        <HowToConnect />
      </DialogContent>
    </Dialog>
  )
}

function Connections() {
  const { t, i18n } = useLingui()
  // query: direct the slot route's own read (see its loader)
  const { data: connections } = useSuspenseQuery(trpc.mcp.listConnections.queryOptions())
  const revoke = useMutation(trpc.mcp.revokeConnection.mutationOptions())
  const date = (value: string | null) => (value ? formatDateLocale(value, i18n.locale as Locale) : null)
  if (connections.length === 0) {
    return <p className="text-muted-foreground text-sm">{t`No AI app is connected.`}</p>
  }
  return (
    <ul className="flex flex-col gap-1" aria-label={t`Connected AI apps`}>
      {connections.map((connection) => {
        const name = connection.clientName
        const company = connection.acting.companyName
        const created = date(connection.createdAt)
        const used = date(connection.lastUsedAt)
        const acting =
          connection.acting.view === 'company'
            ? company
              ? t`As ${company}`
              : t`As your company`
            : company
              ? t`As yourself, in ${company}`
              : t`As yourself`
        return (
          <li key={connection.grantId} className="bg-muted/30 flex items-center gap-3 rounded-xl px-4 py-3">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="truncate text-sm font-medium">{name}</span>
              <span className="text-muted-foreground text-xs">
                {acting} · {t`Connected ${created}`}
                {used ? ` · ${t`Last used ${used}`}` : ''}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              aria-label={t`Disconnect ${name}`}
              disabled={revoke.isPending}
              onClick={() => revoke.mutate({ grantId: connection.grantId })}
            >
              {t`Disconnect`}
            </Button>
          </li>
        )
      })}
    </ul>
  )
}

function HowToConnect() {
  const { t } = useLingui()
  const url = `${window.location.origin}/api/v2/mcp`
  return (
    <div className="flex flex-col gap-3 border-t pt-4">
      <CopyableCredential label={t`Connector URL`} value={url} />
      <ul className="text-muted-foreground flex flex-col gap-1 text-xs">
        <li>{t`Claude: Settings, Connectors, Add custom connector, and paste the URL.`}</li>
        <li>{t`ChatGPT: Settings, Connectors, Create (developer mode), and paste the URL.`}</li>
        <li>
          {t`Claude Code:`} <code>claude mcp add --transport http danchiano {url}</code>
        </li>
      </ul>
    </div>
  )
}
